// =====================================================
// components/LimitNotification.tsx
// Notification limite posts quotidienne — 7s auto-dismiss
// Réutilisable dans actu.tsx ET profile.tsx
// =====================================================
//
// PATCH i18n — Passage du texte affiché par i18next (useTranslation / t()).
// Icônes, couleurs, IDs et animations restent codés en dur, inchangés.
// `message` reste une prop libre fournie par l'appelant (déjà traduite
// en amont côté écran appelant), donc non touchée ici.

import React, { useEffect, useRef } from 'react';
import {
  StyleSheet, Text, View, TouchableOpacity,
  Animated, Platform,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';

const BRAND_GOLD  = '#FFD700';
const DARK_BG     = '#0D0D1A';
const CARD_BORDER = '#2A2A4A';
const TEXT_MAIN   = '#F0EFF8';
const TEXT_SUB    = '#9491B4';
const ACCENT      = '#7B5FFF';
const PURPLE      = '#7B1FA2';

export default function LimitNotification({ visible, message, isPro, onDismiss }: {
  visible:   boolean;
  message:   string;
  isPro:     boolean;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const router    = useRouter();
  const slideAnim = useRef(new Animated.Value(-160)).current;
  const opacAnim  = useRef(new Animated.Value(0)).current;
  const barAnim   = useRef(new Animated.Value(1)).current;
  const timerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.spring(slideAnim, { toValue: 0, friction: 8, tension: 65, useNativeDriver: true }),
        Animated.timing(opacAnim,  { toValue: 1, duration: 300, useNativeDriver: true }),
      ]).start();
      barAnim.setValue(1);
      Animated.timing(barAnim, { toValue: 0, duration: 7000, useNativeDriver: false }).start();
      timerRef.current = setTimeout(() => handleDismiss(), 7000);
    } else {
      slideAnim.setValue(-160);
      opacAnim.setValue(0);
      if (timerRef.current) clearTimeout(timerRef.current);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [visible]);

  const handleDismiss = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    Animated.parallel([
      Animated.timing(slideAnim, { toValue: -160, duration: 260, useNativeDriver: true }),
      Animated.timing(opacAnim,  { toValue: 0,    duration: 260, useNativeDriver: true }),
    ]).start(() => onDismiss());
  };

  if (!visible) return null;

  return (
    <Animated.View style={[
      styles.wrapper,
      { transform: [{ translateY: slideAnim }], opacity: opacAnim },
    ]}>
      {/* Barre de progression */}
      <Animated.View style={[
        styles.progressBar,
        {
          backgroundColor: isPro ? BRAND_GOLD : ACCENT,
          width: barAnim.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }),
        },
      ]} />

      <View style={styles.inner}>
        {/* Icône */}
        <View style={[styles.iconBox, { backgroundColor: isPro ? '#FFD70022' : '#7B1FA222' }]}>
          {isPro
            ? <MaterialCommunityIcons name="crown" size={22} color={BRAND_GOLD} />
            : <Ionicons name="time-outline" size={22} color={PURPLE} />
          }
        </View>

        {/* Texte */}
        <View style={styles.textBlock}>
          <Text style={styles.title}>
            {isPro ? t('limitNotification.titlePro') : t('limitNotification.titleDaily')}
          </Text>
          <Text style={styles.message}>{message}</Text>

          {/* Bouton Passer PRO — uniquement pour non-pro */}
          {!isPro && (
            <TouchableOpacity
              style={styles.upgradeBtn}
              onPress={() => { handleDismiss(); router.push('/recharge'); }}
              activeOpacity={0.8}
            >
              <LinearGradient
                colors={['#FF0080', '#FF8C00']}
                start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}
                style={styles.upgradeBtnGrad}
              >
                <MaterialCommunityIcons name="crown-outline" size={13} color="#fff" />
                <Text style={styles.upgradeBtnText}>{t('limitNotification.upgradeButton')}</Text>
              </LinearGradient>
            </TouchableOpacity>
          )}
        </View>

        {/* Bouton × */}
        <TouchableOpacity style={styles.closeBtn} onPress={handleDismiss} activeOpacity={0.7}>
          <Ionicons name="close" size={18} color="#999" />
        </TouchableOpacity>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position:        'absolute',
    top:             Platform.OS === 'ios' ? 50 : 10,
    left:            16,
    right:           16,
    zIndex:          2000,
    backgroundColor: DARK_BG,
    borderRadius:    18,
    overflow:        'hidden',
    shadowColor:     '#000',
    shadowOffset:    { width: 0, height: 8 },
    shadowOpacity:   0.35,
    shadowRadius:    16,
    elevation:       12,
    borderWidth:     1,
    borderColor:     CARD_BORDER,
  },
  progressBar: { height: 3 },
  inner:       { flexDirection: 'row', alignItems: 'flex-start', padding: 14, gap: 12 },
  iconBox:     { width: 44, height: 44, borderRadius: 13, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  textBlock:   { flex: 1, gap: 4 },
  title:       { fontSize: 14, fontWeight: '800', color: TEXT_MAIN, letterSpacing: 0.2 },
  message:     { fontSize: 13, color: TEXT_SUB, lineHeight: 18 },
  upgradeBtn:  { marginTop: 8, alignSelf: 'flex-start', borderRadius: 10, overflow: 'hidden' },
  upgradeBtnGrad:  { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 7 },
  upgradeBtnText:  { fontSize: 12, fontWeight: '800', color: '#fff', letterSpacing: 0.3 },
  closeBtn:    { width: 28, height: 28, borderRadius: 14, backgroundColor: '#2A2A4A', justifyContent: 'center', alignItems: 'center', flexShrink: 0, marginTop: 2 },
});
